
import { useState, lazy, Suspense } from 'react';

// Lazy load components that are below the fold
const TestimonialsSection = lazy(() => import('./components/TestimonialsSection'));
const MediaSection = lazy(() => import('./components/MediaSection'));
const TestingSection = lazy(() => import('./components/TestingSection'));

export default function Home() {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const testimonials = [
    {
      name: "Prof.ª Dr.ª Telma Evangelista de Araújo",
      role: "Orientadora - PPGEnf/UFPI",
      text: "O VacinAção® representa um marco na digitalização da saúde, resultado de décadas de experiência com vacinas e pesquisa científica rigorosa."
    },
    {
      name: "Enfermeira da Sala de Vacina",
      role: "Rede Pública de Teresina",
      text: "O aplicativo facilita muito nosso trabalho no controle do cartão de vacinas das crianças e orienta os pais de forma clara e confiável."
    },
    {
      name: "Mãe Participante dos Testes",
      role: "Usuária do VacinAção®",
      text: "Finalmente tenho informações seguras sobre vacinas! O app me ajuda a não acreditar em fake news e manter a vacinação em dia."
    }
  ];

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const scrollToDownload = () => {
    const downloadSection = document.getElementById('download');
    if (downloadSection) {
      downloadSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header - Critical above-the-fold */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img 
              src="https://static.readdy.ai/image/ec79395c9f9f8baecd4eee2a0311f508/61722c86ac099ca23aab3e9a68ffb765.png" 
              alt="VacinAção® Logo"
              className="w-10 h-10 object-contain"
              width="40"
              height="40"
              loading="eager"
            />
            <span className="text-2xl font-bold text-blue-600" style={{fontFamily: "Inter, sans-serif", fontWeight: "700"}}>VacinAção®</span>
          </div>
          <nav className="hidden md:flex items-center space-x-8">
            <a href="#inicio" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">Início</a>
            <a href="#sobre" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">Sobre</a>
            <a href="#funcionalidades" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">Funcionalidades</a>
            <a href="#premio" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">Prêmio</a>
            <a href="#contato" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">Contato</a>
          </nav>
          <button className="md:hidden p-2">
            <i className="ri-menu-line text-2xl text-gray-700"></i>
          </button>
        </div>
      </header>

      {/* Hero Section - Critical above-the-fold content */}
      <section id="inicio" className="relative bg-gradient-to-br from-blue-50 to-indigo-100 py-20 overflow-hidden">
        <div className="relative max-w-6xl mx-auto px-4 flex items-center min-h-[600px]">
          <div className="w-full grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                  Combate à Hesitação
                  <span className="text-blue-600 block">Vacinal</span>
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  O VacinAção® é o aplicativo premiado no 27º CBCENF que oferece informações confiáveis sobre imunização e combate às fake news sobre vacinas.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={scrollToDownload}
                  className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-download-line"></i>
                  <span>Baixar Aplicativo</span>
                </button>
                <button className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors whitespace-nowrap cursor-pointer">
                  Saiba Mais
                </button>
              </div>

              <div className="flex items-center space-x-8 pt-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600">8</div>
                  <div className="text-sm text-gray-600">Salas de Vacina</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600">45k+</div>
                  <div className="text-sm text-gray-600">Participantes CBCENF</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600">1º</div>
                  <div className="text-sm text-gray-600">Lugar Prêmio</div>
                </div>
              </div>
            </div>

            <div className="flex justify-center items-center">
              <div className="relative">
                <img 
                  src="https://static.readdy.ai/image/ec79395c9f9f8baecd4eee2a0311f508/319812aecda59a99ad24ea756e54cc8a.jfif" 
                  alt="VacinAção® App"
                  className="w-64 sm:w-80 h-auto rounded-3xl shadow-2xl"
                  width="320"
                  height="640"
                  loading="eager"
                />
                <div className="absolute -top-4 -right-4 bg-green-500 text-white p-3 rounded-full">
                  <i className="ri-check-line text-2xl"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section - Optimized images */}
      <section id="sobre" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Sobre o VacinAção®</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Desenvolvido no contexto do Programa de Pós-Graduação em Enfermagem da UFPI, o VacinAção® nasce da tese de doutorado de Eugênio Barbosa de Melo Júnior, orientado pela Prof.ª Dr.ª Telma Evangelista de Araújo.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-blue-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Combate às Fake News</h3>
                    <p className="text-gray-600">
                      Oferece informações confiáveis sobre imunização, baseadas em evidências científicas, 
                      para combater a desinformação que afeta as coberturas vacinais.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-heart-line text-green-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Para Pais e Profissionais</h3>
                    <p className="text-gray-600">
                      Auxilia pais, mães e responsáveis no acompanhamento do calendário vacinal infantil, 
                      além de apoiar profissionais de enfermagem em salas de vacinação.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-microscope-line text-purple-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Base Científica</h3>
                    <p className="text-gray-600">
                      Resultado de pesquisa acadêmica rigorosa no PPG em Enfermagem da UFPI, 
                      com orientação de professora especialista em vacinas há décadas.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-8">
              <img 
                src="https://public.readdy.ai/ai/img_res/e24078b071cfbd86e1693027ecd30f50.jpg" 
                alt="Pesquisa VacinAção®"
                className="w-full rounded-2xl shadow-lg"
                width="600"
                height="400"
                loading="lazy"
              />
              
              <div className="bg-blue-50 p-8 rounded-2xl">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Nossa Missão</h3>
                <p className="text-gray-700 leading-relaxed">
                  Contribuir para o aumento das coberturas vacinais no Brasil através de uma tecnologia que 
                  empodere famílias com informações confiáveis e apoie profissionais de enfermagem no cuidado com a imunização infantil.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Research Team Section - Lazy loaded images */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Nossa Equipe</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Conheça os profissionais apaixonados por saúde e tecnologia que tornaram o VacinAção® uma realidade
            </p>
          </div>

          {/* Highlighted Founder */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-12 rounded-3xl text-center relative overflow-hidden">
              <div className="absolute top-4 right-4 bg-yellow-400 text-yellow-900 px-4 py-2 rounded-full text-sm font-semibold">
                <i className="ri-lightbulb-line mr-2"></i>
                Idealizador
              </div>
              <div className="mb-8">
                <img 
                  src="https://static.readdy.ai/image/ec79395c9f9f8baecd4eee2a0311f508/ff28b371fcba3dc3b5400788da876ab4.png" 
                  alt="Eugênio Barbosa de Melo Júnior"
                  className="w-32 h-32 rounded-full mx-auto object-cover object-top mb-6 ring-4 ring-blue-200"
                  width="128"
                  height="128"
                  loading="lazy"
                />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Eugênio Barbosa de Melo Júnior</h3>
                <p className="text-blue-600 font-semibold text-lg mb-4">Pós-doutorando PPGEnf/UFPI | Autor do VacinAção®</p>
                <p className="text-gray-700 max-w-2xl mx-auto leading-relaxed">
                  Enfermeiro e professor com vivência prática em salas de vacinação. Desenvolveu o VacinAção® como resultado 
                  de sua tese de doutorado defendida em 2023, sob orientação da Prof.ª Dr.ª Telma Evangelista de Araújo. O trabalho foi 
                  premiado em 1º lugar no 27º CBCENF na categoria "Inovação, tecnologia e empreendedorismo".
                </p>
              </div>
              <div className="flex justify-center items-center space-x-8">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">Autor</div>
                  <div className="text-sm text-gray-600">do Projeto</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">2023</div>
                  <div className="text-sm text-gray-600">Tese Defendida</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">1º</div>
                  <div className="text-sm text-gray-600">Lugar CBCENF</div>
                </div>
              </div>
            </div>
          </div>

          {/* Continue with rest of team section - optimize images */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-12 rounded-3xl text-center relative overflow-hidden">
              <div className="absolute top-4 right-4 bg-purple-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                <i className="ri-graduation-cap-line mr-2"></i>
                Orientadora
              </div>
              <div className="mb-8">
                <img 
                  src="https://static.readdy.ai/image/ec79395c9f9f8baecd4eee2a0311f508/24ad28c1739480c1d306cebf95a0ea79.jfif" 
                  alt="Prof.ª Telma Evangelista de Araújo"
                  className="w-32 h-32 rounded-full mx-auto object-cover object-center mb-6 ring-4 ring-purple-200"
                  width="128"
                  height="128"
                  loading="lazy"
                />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Prof.ª Dr.ª Telma Evangelista de Araújo</h3>
                <p className="text-purple-600 font-semibold text-lg mb-4">Orientadora PPGEnf/UFPI | Especialista em Vacinas</p>
                <p className="text-gray-700 max-w-2xl mx-auto leading-relaxed">
                  Professora com décadas de experiência em pesquisa sobre vacinas e imunização. Orientadora da tese de doutorado 
                  que deu origem ao VacinAção®, contribuindo com sua vasta expertise científica para o desenvolvimento de uma 
                  tecnologia que combate a hesitação vacinal com base em evidências sólidas.
                </p>
              </div>
              <div className="flex justify-center items-center space-x-8">
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">Décadas</div>
                  <div className="text-sm text-gray-600">de Experiência</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">PPGEnf</div>
                  <div className="text-sm text-gray-600">UFPI</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">Vacinas</div>
                  <div className="text-sm text-gray-600">Especialista</div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow text-center">
              <div className="mb-6">
                <img 
                  src="https://static.readdy.ai/image/ec79395c9f9f8baecd4eee2a0311f508/4e0380d410b7227a2088df696e695385.jfif" 
                  alt="Guilherme Feitosa"
                  className="w-20 h-20 rounded-full mx-auto object-cover object-top"
                  width="80"
                  height="80"
                  loading="lazy"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Guilherme Feitosa</h3>
              <p className="text-blue-600 font-medium mb-3">Desenvolvedor Mobile</p>
              <p className="text-gray-600 text-sm">
                Especialista em desenvolvimento de aplicações móveis nativas e híbridas.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow text-center">
              <div className="mb-6">
                <img 
                  src="https://static.readdy.ai/image/ec79395c9f9f8baecd4eee2a0311f508/d9e56f6d86c968251f8a92f3868a43b5.jfif" 
                  alt="Marcos Paulo"
                  className="w-20 h-20 rounded-full mx-auto object-cover object-top"
                  width="80"
                  height="800"
                  loading="lazy"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Marcos Paulo</h3>
              <p className="text-blue-600 font-medium mb-3">Engenheiro de Software</p>
              <p className="text-gray-600 text-sm">
                Especialista em desenvolvimento de sistemas e soluções digitais escaláveis.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow text-center">
              <div className="mb-6">
                <img 
                  src="https://static.readdy.ai/image/ec79395c9f9f8baecd4eee2a0311f508/0fb0a6b2d42d235f9f7ac4662e2b5d0d.jfif" 
                  alt="Jonnison Ferreira"
                  className="w-20 h-20 rounded-full mx-auto object-cover object-top"
                  width="80"
                  height="80"
                  loading="lazy"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Jonnison Ferreira</h3>
              <p className="text-blue-600 font-medium mb-3">Desenvolvedor Backend</p>
              <p className="text-gray-600 text-sm">
                Especialista em APIs seguras e gerenciamento de dados sensíveis.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow text-center">
              <div className="mb-6">
                <img 
                  src="https://static.readdy.ai/image/ec79395c9f9f8baecd4eee2a0311f508/66662b134ceb8932e6b8c7648cd26e56.jfif" 
                  alt="Lucas Sousa"
                  className="w-20 h-20 rounded-full mx-auto object-cover object-top"
                  width="80"
                  height="80"
                  loading="lazy"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Lucas Sousa</h3>
              <p className="text-blue-600 font-medium mb-3">Desenvolvedor Backend</p>
              <p className="text-gray-600 text-sm">
                Desenvolvedor focado em infraestrutura backend e otimização de performance
              </p>
            </div>
          </div>

          <div className="mt-16 bg-white p-12 rounded-3xl shadow-sm">
            <div className="text-center max-w-4xl mx-auto">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-hearts-line text-blue-600 text-2xl"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">Unidos pela Saúde Pública</h3>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Nossa equipe multidisciplinar combina conhecimento técnico avançado com profunda compreensão 
                das necessidades reais do sistema de saúde brasileiro. Cada membro contribui com sua expertise 
                única para criar uma solução verdadeiramente impactante.
              </p>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">2+</div>
                  <div className="text-gray-600">Anos de Desenvolvimento</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">1000+</div>
                  <div className="text-gray-600">Horas de Pesquisa</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">50+</div>
                  <div className="text-gray-600">Testes com Usuários</div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Features Section */}
      <section id="funcionalidades" className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Funcionalidades Principais</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Descubra como o VacinAção® combate a hesitação vacinal e facilita o acompanhamento da imunização infantil.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
              <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-shield-cross-line text-red-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Combate Fake News</h3>
              <p className="text-gray-600">
                Oferece informações confiáveis sobre imunização baseadas em evidências científicas para combater a desinformação.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-calendar-check-line text-blue-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Calendário Vacinal</h3>
              <p className="text-gray-600">
                Auxilia pais e responsáveis no acompanhamento completo do calendário vacinal de crianças menores de 5 anos.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-user-heart-line text-green-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Apoio Profissional</h3>
              <p className="text-gray-600">
                Apoia profissionais de enfermagem em salas de vacinação no controle e orientação sobre imunização.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-smartphone-line text-purple-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Fácil Utilização</h3>
              <p className="text-gray-600">
                Interface intuitiva desenvolvida para ser acessível a todos os usuários, facilitando o controle vacinal.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
              <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-microscope-line text-orange-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Base Científica</h3>
              <p className="text-gray-600">
                Desenvolvido com rigor acadêmico no PPG em Enfermagem da UFPI, garantindo confiabilidade das informações.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-heart-pulse-line text-teal-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Saúde Pública</h3>
              <p className="text-gray-600">
                Contribui para o aumento das coberturas vacinais e fortalecimento da saúde pública brasileira.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Award Section */}
      <section id="premio" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-3xl p-12 text-center">
            <div className="mb-8">
              <div className="w-24 h-24 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-trophy-line text-yellow-900 text-4xl"></i>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Prêmios e Reconhecimentos</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                O VacinAção® conquistou múltiplos prêmios, demonstrando sua excelência acadêmica e impacto na saúde pública brasileira.
              </p>
            </div>

            {/* Main Awards Grid */}
            <div className="grid lg:grid-cols-3 gap-8 mb-12">
              {/* CBCENF Award */}
              <div className="bg-white p-8 rounded-2xl shadow-sm border-2 border-yellow-200">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-trophy-line text-yellow-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">1º Lugar CBCENF</h3>
                <p className="text-gray-600 mb-4">
                  <strong>27º Congresso Brasileiro dos Conselhos de Enfermagem</strong>
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  Categoria: "Inovação, tecnologia e empreendedorismo nos processos de trabalho da Enfermagem"
                </p>
                <div className="bg-yellow-50 p-3 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">45k+</div>
                  <div className="text-xs text-gray-600">Participantes do evento</div>
                </div>
              </div>

              {/* PPG Enfermagem Award */}
              <div className="bg-white p-8 rounded-2xl shadow-sm border-2 border-blue-200">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-graduation-cap-line text-blue-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Melhor Tese 2023</h3>
                <p className="text-gray-600 mb-4">
                  <strong>Programa de Pós-Graduação em Enfermagem da UFPI</strong>
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  Reconhecimento pela excelência da pesquisa em enfermagem
                </p>
                <div className="bg-blue-50 p-3 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">2023</div>
                  <div className="text-xs text-gray-600">Ano da premiação</div>
                </div>
              </div>

              {/* UFPI Award */}
              <div className="bg-white p-8 rounded-2xl shadow-sm border-2 border-purple-200">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-microscope-line text-purple-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Melhor Tese UFPI</h3>
                <p className="text-gray-600 mb-4">
                  <strong>Universidade Federal do Piauí</strong>
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  Categoria: "Ciências da Vida" - 2023
                </p>
                <div className="bg-purple-50 p-3 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">2023</div>
                  <div className="text-xs text-gray-600">Categoria Ciências da Vida</div>
                </div>
              </div>
            </div>

            {/* Recognition Stats */}
            <div className="grid md:grid-cols-4 gap-6 mb-12">
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-600 mb-2">3</div>
                <div className="text-gray-600 text-sm">Prêmios Conquistados</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">2023</div>
                <div className="text-gray-600 text-sm">Ano de Reconhecimento</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600 mb-2">Nacional</div>
                <div className="text-gray-600 text-sm">Alcance dos Prêmios</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600 mb-2">Inovação</div>
                <div className="text-gray-600 text-sm">Foco Principal</div>
              </div>
            </div>

            {/* Quote Section */}
            <div className="bg-white p-8 rounded-2xl shadow-sm max-w-4xl mx-auto">
              <blockquote className="text-lg text-gray-700 italic mb-4">
                "Receber três prêmios importantes em 2023 demonstra que o VacinAção® não é apenas uma pesquisa acadêmica, 
                mas uma solução real para um problema urgente da saúde pública brasileira. É um reconhecimento que nos 
                motiva a continuar inovando e impactando vidas."
              </blockquote>
              <div className="text-sm text-gray-500">
                - Eugênio Barbosa de Melo Júnior, autor do VacinAção®
              </div>
            </div>

            {/* Timeline of Awards */}
            <div className="mt-12 bg-gradient-to-r from-gray-50 to-blue-50 p-8 rounded-2xl">
              <h4 className="text-2xl font-semibold text-gray-900 mb-8">Cronologia dos Prêmios</h4>
              <div className="flex flex-col md:flex-row justify-center items-center space-y-6 md:space-y-0 md:space-x-8">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold">1</div>
                  <div className="text-left">
                    <div className="font-semibold text-gray-900">Melhor Tese PPGEnf</div>
                    <div className="text-sm text-gray-600">2023</div>
                  </div>
                </div>
                
                <div className="hidden md:block w-8 h-0.5 bg-gray-300"></div>
                
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">2</div>
                  <div className="text-left">
                    <div className="font-semibold text-gray-900">Melhor Tese UFPI</div>
                    <div className="text-sm text-gray-600">2023</div>
                  </div>
                </div>
                
                <div className="hidden md:block w-8 h-0.5 bg-gray-300"></div>
                
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center text-white font-bold">3</div>
                  <div className="text-left">
                    <div className="font-semibold text-gray-900">1º Lugar CBCENF</div>
                    <div className="text-sm text-gray-600">2023</div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Lazy loaded sections */}
      <Suspense fallback={<div className="py-20 bg-gray-50 flex items-center justify-center"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>}>
        <MediaSection />
      </Suspense>

      <Suspense fallback={<div className="py-20 bg-white flex items-center justify-center"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>}>
        <TestingSection />
      </Suspense>

      <Suspense fallback={<div className="py-20 bg-gray-50 flex items-center justify-center"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>}>
        <TestimonialsSection 
          testimonials={testimonials}
          currentTestimonial={currentTestimonial}
          setCurrentTestimonial={setCurrentTestimonial}
          nextTestimonial={nextTestimonial}
          prevTestimonial={prevTestimonial}
        />
      </Suspense>

      {/* CTA Section - Updated to be the Download Section */}
      <section id="download" className="py-20 bg-blue-600">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Faça parte da luta contra a hesitação vacinal
          </h2>
          <p className="text-xl text-blue-100 mb-12 max-w-3xl mx-auto">
            Baixe o VacinAção®, o aplicativo premiado que combate fake news e oferece informações 
            confiáveis sobre imunização infantil
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <a 
              href="https://play.google.com/store/apps/details?id=com.cerebro.vaccination"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors flex items-center space-x-3 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-google-play-line text-2xl"></i>
              <div className="text-left">
                <div className="text-xs">Disponível no</div>
                <div>Google Play</div>
              </div>
            </a>
            
            <a 
              href="https://apps.apple.com/br/app/vacina%C3%A7%C3%A3o/id6746348605"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors flex items-center space-x-3 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-apple-line text-2xl"></i>
              <div className="text-left">
                <div className="text-xs">Baixar na</div>
                <div>App Store</div>
              </div>
            </a>
          </div>

          <div className="mt-12 text-blue-100 text-sm">
            <p>Atualmente em fase de testes nas salas de vacina de Teresina</p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contato" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Entre em Contato</h2>
            <p className="text-xl text-gray-600">
              Tem dúvidas sobre o VacinAção® ou quer saber mais sobre o projeto?
            </p>
          </div>

          <div className="flex justify-center">
            <div className="max-w-md w-full">
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-12 rounded-3xl text-center shadow-sm">
                <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-8">
                  <i className="ri-whatsapp-line text-white text-3xl"></i>
                </div>
                
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Fale Conosco</h3>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  Entre em contato diretamente pelo WhatsApp para tirar suas dúvidas sobre o VacinAção® ou conhecer mais sobre nosso projeto premiado.
                </p>
                
                <a 
                  href="https://wa.me/5589999053331"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-500 text-white px-8 py-4 rounded-xl font-semibold hover:bg-green-600 transition-colors flex items-center justify-center space-x-3 whitespace-nowrap cursor-pointer text-lg"
                >
                  <i className="ri-whatsapp-line text-xl"></i>
                  <span>Chamar no WhatsApp</span>
                </a>
                
                <div className="mt-6 text-green-700 font-medium">
                  +55 (89) 99905-3331
                </div>
                
                <div className="mt-4 text-sm text-gray-500">
                  Resposta rápida durante horário comercial
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <img 
                  src="https://static.readdy.ai/image/ec79395c9f9f8baecd4eee2a0311f508/61722c86ac099ca23aab3e9a68ffb765.png" 
                  alt="VacinAção® Logo"
                  className="w-8 h-8 object-contain"
                  width="32"
                  height="32"
                  loading="lazy"
                />
                <span className="text-xl font-bold" style={{fontFamily: "Inter, sans-serif", fontWeight: "700"}}>VacinAção®</span>
              </div>
              <p className="text-gray-400">
                Aplicativo premiado no 27º CBCENF que combate a hesitação vacinal através de informações científicas confiáveis.
              </p>
              <div className="flex space-x-4">
                <button className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-facebook-line text-xl"></i>
                </button>
                <button className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-xl"></i>
                </button>
                <button className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-linkedin-line text-xl"></i>
                </button>
                <button className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-twitter-line text-xl"></i>
                </button>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Projeto</h3>
              <ul className="space-y-2">
                <li><a href="#funcionalidades" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Funcionalidades</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Download</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Testes</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Pesquisa</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Instituição</h3>
              <ul className="space-y-2">
                <li><a href="#sobre" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Sobre</a></li>
                <li><a href="#premio" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Prêmio CBCENF</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">PPG Enfermagem</a></li>
                <li><a href="#contato" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Contato</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Links</h3>
              <ul className="space-y-2">
                <li><a href="https://ufpi.br/" className="text-gray-400 hover:text-white transition-colors cursor-pointer">UFPI</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">CBCENF</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Pesquisas</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Publicações</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="text-gray-400 text-sm">
                © 2024 VacinAção®. Projeto PPG Enfermagem UFPI. Todos os direitos reservados.
              </div>
              <div className="flex items-center space-x-6">
                <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer">Política de Privacidade</a>
                <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer">Termos de Uso</a>
                <a href="https://readdy.ai/?origin=logo" className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer">Made with Readdy</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
